import React from 'react';

import { CarOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Input, InputNumber, Select } from 'antd';

import {
  defaultVoltage,
  newConnector,
  PHASES_BY_SYSTEM,
  PhaseSystem,
  SimChargeBox,
  SimConnector,
  stationPhaseCount,
  SYSTEM_LABELS,
} from './scenario';
import * as S from './styled';

import {
  carGridPhases,
  describePhaseRotation,
  phaseRotationLabel,
} from '../../../utils/phaseRotation/phaseRotation';

const CAR_PHASE_LABELS: Record<number, string> = {
  1: 'Carro monofásico (1 fase)',
  2: 'Carro bifásico (2 fases)',
  3: 'Carro trifásico (3 fases)',
};

const MAX_CONNECTORS = 2;

type StationEditorProps = {
  chargeBox: SimChargeBox;
  onChange: (chargeBox: SimChargeBox) => void;
  onRemove: () => void;
};

export const StationEditor = ({ chargeBox, onChange, onRemove }: StationEditorProps) => {
  const phaseCount = stationPhaseCount(chargeBox);
  const system = chargeBox.phase_system || 'THREE_PHASE';

  const updateConnector = (key: string, patch: Partial<SimConnector>) =>
    onChange({
      ...chargeBox,
      connectors: chargeBox.connectors.map((connector) =>
        connector.key === key ? { ...connector, ...patch } : connector,
      ),
    });

  const changeSystem = (value: PhaseSystem) =>
    onChange({
      ...chargeBox,
      phase_system: value,
      phase: PHASES_BY_SYSTEM[value][0],
      voltage_v: defaultVoltage(value),
      // Carro não puxa mais fases do que a estação tem
      connectors: chargeBox.connectors.map((connector) => ({ ...connector, car_phases: null })),
    });

  return (
    <S.StationCard $warning={!chargeBox.phase} data-testid='simulator-station'>
      <S.StationHeader>
        <Input
          size='small'
          value={chargeBox.code}
          aria-label='Nome da estação'
          onChange={(event) => onChange({ ...chargeBox, code: event.target.value })}
        />
        <Button size='small' type='text' aria-label='Remover estação' icon={<DeleteOutlined />} onClick={onRemove} />
      </S.StationHeader>

      <Select
        size='small'
        style={{ width: '100%' }}
        value={system}
        aria-label='Sistema'
        onChange={(value) => changeSystem(value as PhaseSystem)}
        options={(Object.keys(SYSTEM_LABELS) as PhaseSystem[]).map((key) => ({
          value: key,
          label: SYSTEM_LABELS[key],
        }))}
      />

      <Select
        size='small'
        style={{ width: '100%' }}
        value={chargeBox.phase || undefined}
        placeholder='Sem fase cadastrada'
        aria-label='Fase / rotação'
        dropdownMatchSelectWidth={false}
        onChange={(value) => onChange({ ...chargeBox, phase: value as string })}
        options={PHASES_BY_SYSTEM[system].map((phase) => ({
          value: phase,
          label: phaseRotationLabel(phase),
        }))}
      />
      <S.Hint>
        {chargeBox.phase
          ? describePhaseRotation(chargeBox.phase)
          : 'Sem fase cadastrada: o cálculo conta a estação nas três fases.'}
      </S.Hint>

      {chargeBox.connectors.map((connector) => {
        // Sem fase cadastrada o grupo conta o conector nas três fases, qualquer que seja o carro (MV2-1958)
        const gridPhases = chargeBox.phase ? carGridPhases(chargeBox.phase, connector.car_phases) : 'ABC';

        return (
          <S.ConnectorRow key={connector.key} $charging={connector.charging}>
            <S.CarButton
              type='button'
              $charging={connector.charging}
              aria-pressed={connector.charging}
              onClick={() => updateConnector(connector.key, { charging: !connector.charging })}
            >
              <CarOutlined />
              {connector.charging
                ? `Conector ${connector.seq}: carregando (fase ${gridPhases})`
                : `Conector ${connector.seq}: vaga livre`}
            </S.CarButton>

            {connector.charging && chargeBox.phase && phaseCount > 1 && (
              <Select
                size='small'
                style={{ width: '100%' }}
                aria-label='Fases que o carro puxa'
                value={connector.car_phases && connector.car_phases < phaseCount ? connector.car_phases : phaseCount}
                onChange={(value) =>
                  updateConnector(connector.key, { car_phases: Number(value) >= phaseCount ? null : Number(value) })
                }
                options={Array.from({ length: phaseCount }, (_, index) => ({
                  value: index + 1,
                  label: CAR_PHASE_LABELS[index + 1],
                }))}
              />
            )}

            <S.StationHeader>
              <S.Hint style={{ flex: 1 }}>Corrente máxima do conector</S.Hint>
              <InputNumber
                size='small'
                min={6}
                max={500}
                value={connector.current_max_a ?? undefined}
                aria-label='Corrente máxima do conector'
                formatter={(value) => `${value ?? ''} A`}
                parser={(value) => Number((value || '').replace(/[^\d.]/g, ''))}
                onChange={(value) =>
                  updateConnector(connector.key, { current_max_a: value ? Number(value) : null })
                }
              />
            </S.StationHeader>
          </S.ConnectorRow>
        );
      })}

      {chargeBox.connectors.length < MAX_CONNECTORS && (
        <Button
          size='small'
          type='link'
          icon={<PlusOutlined />}
          onClick={() =>
            onChange({
              ...chargeBox,
              connectors: [
                ...chargeBox.connectors,
                newConnector(chargeBox.connectors.length + 1, chargeBox.connectors[0]?.current_max_a ?? 32),
              ],
            })
          }
        >
          Conector
        </Button>
      )}
    </S.StationCard>
  );
};
